import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Search as SearchIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { tmdbApi } from '../../services/tmdb';
import { useDebounce } from '../../hooks/useDebounce';

const SearchContainer = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  
  @media (max-width: 768px) {
    position: ${props => props.$isExpanded ? 'fixed' : 'relative'};
    top: ${props => props.$isExpanded ? '0' : 'auto'};
    left: ${props => props.$isExpanded ? '0' : 'auto'};
    right: ${props => props.$isExpanded ? '0' : 'auto'};
    bottom: ${props => props.$isExpanded ? '0' : 'auto'};
    padding: ${props => props.$isExpanded ? 'var(--spacing-2)' : '0'};
    background: ${props => props.$isExpanded ? 'var(--color-background-paper)' : 'transparent'};
    z-index: 1000;
    display: flex;
    flex-direction: column;
  }
`;

const SearchWrapper = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  background: rgba(30, 30, 30, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 25px;
  transition: all 0.3s ease;
  width: ${props => props.$isExpanded ? '100%' : '250px'};
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: rgba(40, 40, 40, 0.9);
    border-color: rgba(255, 215, 0, 0.3);
  }
  
  &:focus-within {
    background: rgba(40, 40, 40, 0.95);
    border-color: rgba(255, 215, 0, 0.6);
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    width: ${props => props.$isExpanded ? '100%' : '40px'};
    padding: ${props => props.$isExpanded ? '0.5rem 1rem' : '0.5rem'};
    justify-content: ${props => props.$isExpanded ? 'space-between' : 'center'};
    border-radius: ${props => props.$isExpanded ? '12px' : '50%'};
  }
`;

const SearchInput = styled.input<{ $isExpanded: boolean }>`
  background: transparent;
  border: none;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  color: #FFFFFF;
  font-size: 0.95rem;
  font-weight: 400;
  width: 100%;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    display: ${props => props.$isExpanded ? 'block' : 'none'};
    padding: ${props => props.$isExpanded ? '0.75rem' : '0'};
    font-size: 1rem;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
    font-weight: 300;
  }
`;

const CloseButton = styled.button<{ $isExpanded: boolean }>`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.5rem;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: ${props => props.$isExpanded ? 'flex' : 'none'};
    position: absolute;
    right: 0.5rem;
  }
  
  &:hover {
    color: #FFFFFF;
  }
`;

const SearchResults = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.75rem;
  background: linear-gradient(to bottom, rgba(20, 20, 20, 0.98), rgba(15, 15, 15, 0.98));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 215, 0, 0.1);
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  display: ${props => props.$visible ? 'block' : 'none'};
  z-index: 1000;
  
  /* Scrollbar personnalisée */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 215, 0, 0.3);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 215, 0, 0.5);
    }
  }
  
  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 16px 16px 0 0;
    max-height: 60vh;
  }
`;

const SearchResultItem = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  
  &:last-child {
    border-bottom: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, rgba(255, 215, 0, 0.8), rgba(255, 215, 0, 0.3));
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    gap: 1.5rem;
  }
  
  &:hover {
    background: linear-gradient(to right, rgba(255, 215, 0, 0.08), transparent);
    
    &::before {
      transform: scaleY(1);
    }
  }
`;

const ResultImage = styled.img`
  width: 50px;
  height: 75px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  ${SearchResultItem}:hover & {
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(255, 215, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 55px;
    height: 82px;
  }
`;

const ResultInfo = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    padding-right: 1rem;
  }
`;

const ResultTitle = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.35rem;
  line-height: 1.3;
  letter-spacing: 0.2px;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const ResultYear = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ResultType = styled.div`
  padding: 0.4rem 0.75rem;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.25), rgba(255, 215, 0, 0.15));
  color: #FFD700;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  ${SearchResultItem}:hover & {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.35), rgba(255, 215, 0, 0.25));
    border-color: rgba(255, 215, 0, 0.5);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.45rem 0.85rem;
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  left: 0.85rem;
  color: rgba(255, 215, 0, 0.7);
  width: 20px;
  height: 20px;
  transition: color 0.3s ease;
  
  ${SearchWrapper}:hover & {
    color: rgba(255, 215, 0, 0.9);
  }
  
  ${SearchWrapper}:focus-within & {
    color: rgba(255, 215, 0, 1);
  }
`;

export const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: searchResults, isLoading, error } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: async () => {
      console.log('🔍 Fetching search results for:', debouncedQuery);
      const results = await tmdbApi.searchMulti(debouncedQuery);
      console.log('✅ Search results received:', results);
      return results;
    },
    enabled: debouncedQuery.length >= 2, // Minimum 2 caractères
    retry: 1,
  });

  // Debug logs
  useEffect(() => {
    console.log('📊 Search state:', {
      query,
      debouncedQuery,
      isLoading,
      hasResults: !!searchResults,
      resultsCount: searchResults?.results?.length || 0,
      error: error?.message
    });
  }, [query, debouncedQuery, searchResults, isLoading, error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length > 0);
  };

  const handleResultClick = (type: string, id: number) => {
    setIsOpen(false);
    setQuery('');
    setIsExpanded(false);
    navigate(`/${type === 'movie' ? 'movie' : 'tv'}/${id}`);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (query.trim().length > 0) {
      setIsOpen(false);
      setIsExpanded(false);
      navigate(`/browse?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (isExpanded) {
      setIsOpen(true);
    }
  }, [isExpanded]);

  return (
    <SearchContainer $isExpanded={isExpanded}>
      <SearchWrapper ref={wrapperRef} $isExpanded={isExpanded}>
        <StyledSearchIcon />
        <SearchInput
          placeholder="Rechercher un film ou une série..."
          value={query}
          onChange={handleSearch}
          onFocus={() => setIsOpen(query.length > 0)}
          onKeyPress={handleKeyPress}
          $isExpanded={isExpanded}
        />
        <CloseButton onClick={toggleExpand} $isExpanded={isExpanded}>
          {isExpanded ? 'Fermer' : 'Ouvrir'}
        </CloseButton>
        <SearchResults $visible={isOpen && debouncedQuery.length > 0}>
          {isLoading ? (
            <SearchResultItem>Recherche en cours...</SearchResultItem>
          ) : error ? (
            <SearchResultItem>Erreur lors de la recherche</SearchResultItem>
          ) : !searchResults?.results || searchResults.results.length === 0 ? (
            <SearchResultItem>Aucun résultat trouvé</SearchResultItem>
          ) : (
            searchResults.results.slice(0, 8).map((result: any) => (
              <SearchResultItem
                key={result.id}
                onClick={() => handleResultClick(result.media_type, result.id)}
              >
                <ResultImage
                  src={`https://image.tmdb.org/t/p/w92${result.poster_path}`}
                  alt={result.title || result.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.jpg';
                  }}
                />
                <ResultInfo>
                  <ResultTitle>{result.title || result.name}</ResultTitle>
                  <ResultYear>
                    {(result.release_date || result.first_air_date) 
                      ? new Date(result.release_date || result.first_air_date).getFullYear()
                      : 'N/A'}
                  </ResultYear>
                </ResultInfo>
                <ResultType>
                  {result.media_type === 'movie' ? 'Film' : 'Série'}
                </ResultType>
              </SearchResultItem>
            ))
          )}
        </SearchResults>
      </SearchWrapper>
    </SearchContainer>
  );
};
